import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { saveField, shareFieldWithCounselor } from '../../../services/fieldService';
import { useAuth } from '../../../contexts/AuthContext';

const FieldDetailsModal = ({ field, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [shareMessage, setShareMessage] = useState('');

  if (!field) return null;

  const handleSaveField = async () => {
    if (!user) {
      setSaveMessage('Veuillez vous connecter pour enregistrer une filière');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setSaving(true);
    setSaveMessage('');

    const { data, error } = await saveField(field);

    if (error) {
      if (error?.alreadySaved) {
        setSaveMessage('✓ Cette filière est déjà enregistrée');
      } else {
        setSaveMessage(`Erreur: ${error?.message}`);
      }
    } else {
      setSaveMessage('✓ Filière enregistrée avec succès!');
    }

    setSaving(false);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleShareWithCounselor = async () => {
    if (!user) {
      setShareMessage('Veuillez vous connecter pour partager avec un conseiller');
      setTimeout(() => setShareMessage(''), 3000);
      return;
    }

    setSharing(true);
    setShareMessage('');

    const { data, error } = await shareFieldWithCounselor(field);

    if (error) {
      if (error?.noCounselor) {
        setShareMessage('Aucun conseiller assigné à votre compte');
      } else {
        setShareMessage(`Erreur: ${error?.message}`);
      }
    } else {
      setShareMessage('✓ Filière partagée avec votre conseiller!');
    }

    setSharing(false);
    setTimeout(() => setShareMessage(''), 3000);
  };

  const handleTakeQuiz = () => {
    navigate(`/interactive-quiz-system?field_id=${field?.id}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 md:p-6 rounded-t-xl flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-2 break-words">
              {field?.name}
            </h2>
            <div className="flex items-center gap-2">
              <Icon name="Target" size={20} />
              <span className="text-sm md:text-base">{field?.compatibilityScore}% de compatibilité</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0 ml-4"
            aria-label="Fermer"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-6">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Icon name="FileText" size={24} color="var(--color-primary)" />
              Description complète
            </h3>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {field?.fullDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Icon name="Briefcase" size={20} color="#2563EB" />
                Débouchés professionnels
              </h4>
              <ul className="space-y-2">
                {field?.careerPaths?.map((career, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm md:text-base text-gray-700">
                    <Icon name="CheckCircle2" size={16} color="#10B981" className="mt-1 flex-shrink-0" />
                    <span>{career}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Icon name="TrendingUp" size={20} color="#10B981" />
                Perspectives du marché
              </h4>
              <div className="space-y-3">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Demande actuelle</p>
                  <p className="text-sm md:text-base font-semibold text-gray-800">{field?.marketOutlook}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Salaire moyen</p>
                  <p className="text-sm md:text-base font-semibold text-gray-800">{field?.salaryRange}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Taux d'emploi</p>
                  <p className="text-sm md:text-base font-semibold text-gray-800">{field?.employmentRate}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Icon name="BookOpen" size={20} color="var(--color-primary)" />
              Parcours académique typique
            </h4>
            <div className="space-y-3">
              {field?.academicPath?.map((step, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-semibold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm md:text-base font-medium text-gray-800">{step?.level}</p>
                    <p className="text-xs md:text-sm text-gray-600">{step?.duration} - {step?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Icon name="Award" size={20} color="var(--color-primary)" />
              Compétences développées
            </h4>
            <div className="flex flex-wrap gap-2">
              {field?.skillsDeveloped?.map((skill, index) => (
                <span key={index} className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-xs md:text-sm font-medium">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Icon name="School" size={20} color="var(--color-primary)" />
              Établissements recommandés au Sénégal
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field?.recommendedInstitutions?.map((institution, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                  <p className="text-sm md:text-base font-semibold text-gray-800 mb-1">{institution?.name}</p>
                  <p className="text-xs md:text-sm text-gray-600 mb-2">{institution?.location}</p>
                  <div className="flex items-center gap-2">
                    <Icon name="Star" size={14} color="#F59E0B" />
                    <span className="text-xs text-gray-600">{institution?.rating}/5</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              className="flex-1"
              onClick={handleSaveField}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer cette filière'}
            </Button>
            <Button
              variant="outline"
              iconName="Share2"
              iconPosition="left"
              className="flex-1"
              onClick={handleShareWithCounselor}
              disabled={sharing}
            >
              {sharing ? 'Partage...' : 'Partager avec un conseiller'}
            </Button>
            <Button
              variant="secondary"
              iconName="ClipboardList"
              iconPosition="left"
              className="flex-1"
              onClick={handleTakeQuiz}
            >
              Passer un quiz
            </Button>
          </div>

          {saveMessage && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${
              saveMessage?.startsWith('✓') 
                ? 'bg-green-50 text-green-700 border border-green-200' :'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {saveMessage}
            </div>
          )}

          {shareMessage && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${
              shareMessage?.startsWith('✓') 
                ? 'bg-green-50 text-green-700 border border-green-200' :'bg-yellow-50 text-yellow-700 border border-yellow-200'
            }`}>
              {shareMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldDetailsModal;